import * as MutationHelpers from 'shared/helpers/vuex/mutationHelpers';
import types from '../mutation-types';
import ScheduledMessagesAPI from '../../api/scheduledMessages';
import { responzaDebug } from 'shared/helpers/responzaDebug';

export const state = {
  records: {},
  uiFlags: {
    isFetching: false,
    isCreating: false,
    isUpdating: false,
    isDeleting: false,
  },
};

export const getters = {
  getUIFlags(_state) {
    return _state.uiFlags;
  },
  getScheduledMessagesForConversation: _state => conversationId => {
    const allRecords = Object.values(_state.records);
    const filtered = allRecords.filter(
      record =>
        record.scheduleable_type === 'Conversation' &&
        record.scheduleable_id === conversationId &&
        record.target_scope === 'conversation'
    );

    responzaDebug('[Vuex Getter] getScheduledMessagesForConversation', {
      conversationId,
      conversationIdType: typeof conversationId,
      allRecordsCount: allRecords.length,
      allRecords,
      filteredCount: filtered.length,
      filtered,
      sampleRecord: allRecords[0]
    });

    return filtered;
  },
  getBroadcastMessages: _state => {
    return Object.values(_state.records).filter(
      record => record.target_scope === 'broadcast'
    );
  },
  getScheduledMessage: _state => id => {
    return _state.records[id];
  },
};

export const actions = {
  // Fetch scheduled messages for a conversation
  fetchForConversation: async function fetchScheduledMessagesForConversation(
    { commit },
    payload
  ) {
    const conversationId =
      typeof payload === 'object' ? payload.conversationId : payload;
    const scheduleableId =
      typeof payload === 'object'
        ? payload.scheduleableId ||
          payload.conversationDatabaseId ||
          payload.conversationId
        : payload;

    responzaDebug('[Vuex] fetchForConversation called', {
      conversationId,
      scheduleableId,
    });
    commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isFetching: true });
    try {
      const response = await ScheduledMessagesAPI.getForConversation(
        conversationId
      );
      const scheduledMessages = response.data.scheduled_messages || [];

      responzaDebug('[Vuex] fetchForConversation response', {
        conversationId,
        count: scheduledMessages.length,
        messages: scheduledMessages
      });

      // Convert array to object indexed by id
      const recordsObj = {};
      scheduledMessages.forEach(msg => {
        recordsObj[msg.id] = msg;
      });

      responzaDebug('[Vuex] Committing RESET + SET_SCHEDULED_MESSAGES', {
        conversationId,
        scheduleableId,
        recordsObj
      });

      // Clear existing records for this conversation before merging
      commit(
        types.RESET_SCHEDULED_MESSAGES_FOR_CONVERSATION,
        scheduleableId || conversationId
      );
      commit(types.SET_SCHEDULED_MESSAGES, recordsObj);
    } catch (error) {
      console.error('[Vuex] Error fetching scheduled messages:', error);
    } finally {
      commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isFetching: false });
    }
  },

  // Fetch broadcast messages
  fetchBroadcasts: async function fetchBroadcastMessages({ commit }) {
    commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isFetching: true });
    try {
      const response = await ScheduledMessagesAPI.getBroadcasts();
      const broadcastMessages = response.data.scheduled_broadcasts || [];

      // Convert array to object indexed by id
      const recordsObj = {};
      broadcastMessages.forEach(msg => {
        recordsObj[msg.id] = msg;
      });

      commit(types.SET_SCHEDULED_MESSAGES, recordsObj);
    } catch (error) {
      console.error('Error fetching broadcast messages:', error);
    } finally {
      commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isFetching: false });
    }
  },

  // Create scheduled message for conversation
  createForConversation: async function createScheduledMessageForConversation(
    { commit },
    { conversationId, data, files = [] }
  ) {
    commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isCreating: true });
    try {
      const response = await ScheduledMessagesAPI.createForConversation(
        conversationId,
        data,
        files
      );
      const scheduledMessage = response.data.scheduled_message;
      commit(types.ADD_SCHEDULED_MESSAGE, scheduledMessage);
      return scheduledMessage;
    } catch (error) {
      console.error('Error creating scheduled message:', error);
      throw error;
    } finally {
      commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isCreating: false });
    }
  },

  // Create broadcast message
  createBroadcast: async function createBroadcastMessage(
    { commit },
    { data, files = [] }
  ) {
    commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isCreating: true });
    try {
      const response = await ScheduledMessagesAPI.createBroadcast(data, files);
      const broadcastMessage = response.data.scheduled_broadcast;
      commit(types.ADD_SCHEDULED_MESSAGE, broadcastMessage);
      return broadcastMessage;
    } catch (error) {
      console.error('Error creating broadcast message:', error);
      throw error;
    } finally {
      commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isCreating: false });
    }
  },

  // Update scheduled message
  update: async function updateScheduledMessage(
    { commit },
    { conversationId, id, data, isBroadcast = false }
  ) {
    commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isUpdating: true });
    try {
      const response = isBroadcast
        ? await ScheduledMessagesAPI.updateBroadcast(id, data)
        : await ScheduledMessagesAPI.updateForConversation(
            conversationId,
            id,
            data
          );

      const updatedMessage = isBroadcast
        ? response.data.scheduled_broadcast
        : response.data.scheduled_message;

      commit(types.EDIT_SCHEDULED_MESSAGE, updatedMessage);
      return updatedMessage;
    } catch (error) {
      console.error('Error updating scheduled message:', error);
      throw error;
    } finally {
      commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isUpdating: false });
    }
  },

  // Delete scheduled message
  delete: async function deleteScheduledMessage(
    { commit },
    { conversationId, id, isBroadcast = false }
  ) {
    commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isDeleting: true });
    try {
      if (isBroadcast) {
        await ScheduledMessagesAPI.deleteBroadcast(id);
      } else {
        await ScheduledMessagesAPI.deleteForConversation(conversationId, id);
      }

      commit(types.DELETE_SCHEDULED_MESSAGE, id);
    } catch (error) {
      console.error('Error deleting scheduled message:', error);
      throw error;
    } finally {
      commit(types.SET_SCHEDULED_MESSAGES_UI_FLAG, { isDeleting: false });
    }
  },
};

export const mutations = {
  [types.SET_SCHEDULED_MESSAGES_UI_FLAG](_state, data) {
    _state.uiFlags = {
      ..._state.uiFlags,
      ...data,
    };
  },

  [types.SET_SCHEDULED_MESSAGES](_state, data) {
    responzaDebug('[Vuex Mutation] SET_SCHEDULED_MESSAGES', {
      oldRecords: _state.records,
      newData: data,
      mergedRecords: { ..._state.records, ...data }
    });
    _state.records = {
      ..._state.records,
      ...data,
    };
  },

  [types.ADD_SCHEDULED_MESSAGE](_state, data) {
    responzaDebug('[Vuex Mutation] ADD_SCHEDULED_MESSAGE', {
      oldRecords: _state.records,
      newMessage: data,
      mergedRecords: { ..._state.records, [data.id]: data }
    });
    _state.records = {
      ..._state.records,
      [data.id]: data,
    };
  },

  [types.EDIT_SCHEDULED_MESSAGE]: MutationHelpers.update,

  [types.DELETE_SCHEDULED_MESSAGE](_state, id) {
    const { [id]: toDelete, ...records } = _state.records;
    _state.records = records;
  },

  [types.RESET_SCHEDULED_MESSAGES_FOR_CONVERSATION](
    _state,
    conversationId
  ) {
    if (!conversationId) return;
    const filtered = {};
    Object.entries(_state.records).forEach(([id, record]) => {
      const isSameConversation =
        record.scheduleable_type === 'Conversation' &&
        Number(record.scheduleable_id) === Number(conversationId) &&
        record.target_scope === 'conversation';

      if (!isSameConversation) {
        filtered[id] = record;
      }
    });
    _state.records = filtered;
  },
};

export default {
  namespaced: true,
  actions,
  state,
  getters,
  mutations,
};
